import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Table,
  Space,
  InputNumber,
  message,
  AutoComplete,
  Divider,
  Modal,
  Tooltip,
  Tag,
  Spin
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { invoiceService, productService, stockService } from '../../services';
import { formatCurrency } from '../../utils/helpers';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const InvoiceImportForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // States for calculations
  const [total, setTotal] = useState(0);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debug selectedItems changes
  useEffect(() => {
    console.log('🔄 SELECTED ITEMS CHANGED:');
    console.log('Count:', selectedItems.length);
    console.log('Items:', selectedItems.map(item => ({ id: item.id, name: item.name })));
  }, [selectedItems]);

  // Debug logs
  //console.log('🔧 Component state:', { id, isEditing, selectedItemsCount: selectedItems.length });
  //console.log('📋 Services available:', { invoiceService: !!invoiceService, stockService: !!stockService, productService: !!productService });

  useEffect(() => {
    //console.log('🚀 Component mounted, isEditing:', isEditing, 'id:', id);
    loadProducts();
    if (isEditing) {
      //console.log('📝 Editing mode detected, loading invoice detail...');
      loadInvoiceDetail();
    }
  }, [id]); // Add id as dependency

  useEffect(() => {
    calculateTotal();
  }, [selectedItems]);
  
  // If editing but no ID, redirect back (after hooks)
  useEffect(() => {
    if (isEditing && !id) {
      //console.error('❌ Editing mode but no ID provided');
      message.error('Không tìm thấy ID hóa đơn');
      navigate('/invoices/import');
    }
  }, [isEditing, id, navigate]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      //console.log('🔄 Loading products/variants...');
      
      // Check token first
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        //console.error('❌ No access token found');
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
        return;
      }
      
      // Thử nhiều API theo thứ tự ưu tiên với retry logic
      let response;
      let apiUsed = 'unknown';
      let retryCount = 0;
      const maxRetries = 2;
      
      const tryAPI = async (apiCall, apiName) => {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            //console.log(`🔄 Trying ${apiName} (attempt ${attempt + 1}/${maxRetries + 1})...`);
            const result = await apiCall();
            //console.log(`✅ ${apiName} success:`, result);
            return result;
          } catch (error) {
            //console.log(`⚠️ ${apiName} failed (attempt ${attempt + 1}):`, error.message);
            
            // Nếu là lỗi token và còn lần retry, đợi một chút cho refresh token xử lý
            if ((error.response?.status === 401 || 
                 error.response?.data?.message?.includes('Token không hợp lệ') ||
                 error.response?.data?.message?.includes('đã hết hạn')) && 
                attempt < maxRetries) {
              //console.log(`⏰ Token error detected, waiting for refresh token process...`);
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            }
            
            // Nếu đã hết retry hoặc không phải lỗi token, throw error
            if (attempt === maxRetries) {
              throw error;
            }
          }
        }
      };
      
      // 1. Thử stockService.getVariants trước
      try {
        response = await tryAPI(
          () => stockService.getVariants({ limit: 1000 }), 
          'stockService.getVariants'
        );
        apiUsed = 'stockService.getVariants';
      } catch (stockError) {
        //console.log('⚠️ Stock variants failed completely:', stockError.message);
        
        // 2. Fallback to productService.getFull
        try {
          response = await tryAPI(
            () => productService.getFull({ limit: 1000 }), 
            'productService.getFull'
          );
          apiUsed = 'productService.getFull';
        } catch (getFullError) {
          //console.log('⚠️ getFull failed completely:', getFullError.message);
          
          // 3. Fallback to productService.getAll
          try {
            response = await tryAPI(
              () => productService.getAll({ limit: 1000 }), 
              'productService.getAll'
            );
            apiUsed = 'productService.getAll';
          } catch (getAllError) {
            //console.log('⚠️ getAll failed completely:', getAllError.message);
            
            // 4. Final fallback to productService.searchAdvanced
            try {
              response = await tryAPI(
                () => productService.searchAdvanced({ 
                  page: 1,
                  pageSize: 1000,
                  sortBy: 'name',
                  sortDir: 'asc'
                }), 
                'productService.searchAdvanced'
              );
              apiUsed = 'productService.searchAdvanced';
            } catch (searchError) {
              //console.error('� All product APIs failed:', searchError.message);
              throw new Error('Tất cả API sản phẩm đều không khả dụng. Vui lòng thử lại sau.');
            }
          }
        }
      }
      
      if (response && response.success) {
        const productData = response.data || [];
        //console.log(`✅ Products loaded via ${apiUsed}:`, productData.length, 'items');
        //console.log('🔍 First few products:', productData.slice(0, 3));
        
        setProducts(productData);
        
        if (productData.length === 0) {
          message.info('Chưa có sản phẩm nào trong hệ thống');
        } else {
          message.success(`Đã tải ${productData.length} sản phẩm (${apiUsed})`);
        }
      } else {
        //console.error('❌ Products API error:', response?.message);
        message.error(response?.message || `API ${apiUsed} trả về lỗi: Không thể tải danh sách sản phẩm`);
        setProducts([]);
      }
    } catch (error) {
      //console.error('💥 Error loading products:', error);
      //console.error('💥 Error details:', {
      //   message: error.message,
      //   status: error.response?.status,
      //   statusText: error.response?.statusText,
      //   data: error.response?.data
      // });
      
      // Hiển thị lỗi chi tiết hơn
      let errorMessage = 'Không thể tải danh sách sản phẩm';
      if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.';
        // Auto redirect to login after showing error
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (error.response?.status === 403) {
        errorMessage = 'Không có quyền truy cập API sản phẩm.';
      } else if (error.response?.status === 404) {
        errorMessage = 'API sản phẩm không tồn tại hoặc chưa được cấu hình.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Lỗi server. Vui lòng thử lại sau.';
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Lỗi kết nối mạng. Kiểm tra kết nối internet.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceDetail = async () => {
    setLoading(true);
    try {
      //console.log('📋 Loading invoice detail for ID:', id);
      
      // Check if invoiceService is available
      if (!invoiceService || !invoiceService.import || !invoiceService.import.getById) {
        throw new Error('Invoice service is not available');
      }
      
      // Retry logic for API calls
      const maxRetries = 2;
      
      const tryLoadInvoice = async (attempt = 1) => {
        try {
          // Step 1: Get invoice basic info from list API (contains supplier info)
          //console.log(`🔍 Step 1: Getting invoice basic info from list (attempt ${attempt})...`);
          const listResponse = await invoiceService.import.getAll({ limit: 1000 });
          //console.log('📦 List API Response:', listResponse);
          
          let invoiceBasicInfo = null;
          if (listResponse.success && listResponse.data) {
            // Find the invoice with matching ID
            invoiceBasicInfo = listResponse.data.find(invoice => invoice.id == id);
            //console.log('🎯 Found invoice basic info:', invoiceBasicInfo);
          }
          
          if (!invoiceBasicInfo) {
            throw new Error(`Không tìm thấy hóa đơn với ID: ${id}`);
          }
          
          // Step 2: Get invoice details (products) from detail API
          //console.log(`🔍 Step 2: Getting invoice details (attempt ${attempt})...`);
          const detailResponse = await invoiceService.import.getById(id);
          //console.log('📦 Detail API Response:', detailResponse);
          
          return { invoiceBasicInfo, detailResponse };
        } catch (error) {
          //console.log(`⚠️ Load invoice attempt ${attempt} failed:`, error.message);
          
          // Nếu là lỗi token và còn lần retry, đợi cho refresh token xử lý
          if ((error.response?.status === 401 || 
               error.response?.data?.message?.includes('Token không hợp lệ') ||
               error.response?.data?.message?.includes('đã hết hạn')) && 
              attempt <= maxRetries) {
            //console.log(`⏰ Token error detected, waiting for refresh token process...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            if (attempt < maxRetries) {
              //console.log(`🔄 Retrying load invoice (attempt ${attempt + 1})...`);
              return await tryLoadInvoice(attempt + 1);
            }
          }
          
          throw error;
        }
      };
      
      const { invoiceBasicInfo, detailResponse } = await tryLoadInvoice();
      
      let detailsArray = [];
      if (detailResponse.success && detailResponse.data) {
        // Kiểm tra nếu data là array hoặc object chứa array
        if (Array.isArray(detailResponse.data)) {
          detailsArray = detailResponse.data;
        } else if (detailResponse.data.details && Array.isArray(detailResponse.data.details)) {
          detailsArray = detailResponse.data.details;
        } else if (detailResponse.data.products && Array.isArray(detailResponse.data.products)) {
          detailsArray = detailResponse.data.products;
        }
      }
      
      //console.log('✅ Invoice basic info:', invoiceBasicInfo);
      //console.log('✅ Invoice details loaded:', detailsArray);
      
      // Debug: Log all available fields in invoiceBasicInfo
  //console.log('🔍 Available fields in basic info:', Object.keys(invoiceBasicInfo));
  //console.log('🔍 supplier_name:', invoiceBasicInfo.supplier_name);
  //console.log('🔍 note:', invoiceBasicInfo.note);
      
      // Set form values từ thông tin hóa đơn
      const formData = {
        supplierName: invoiceBasicInfo.supplier_name || '',
        note: invoiceBasicInfo.note || '',
      };
      
      //console.log('📝 Setting form data:', formData);
      form.setFieldsValue(formData);
      
      // Debug: Check if form values were set
      setTimeout(() => {
        const currentValues = form.getFieldsValue();
        //console.log('✅ Current form values after setFieldsValue:', currentValues);
      }, 100);
        
      // Set selected items từ details
      if (detailsArray && detailsArray.length > 0) {
        const items = detailsArray.map(detail => ({
          id: detail.id,
          variant_id: detail.variant_id,
          product_name: detail.product_name || `Product ${detail.product_id}`,
          variant_name: detail.variant_name || detail.sku || `Variant ${detail.variant_id}`,
          quantity: parseInt(detail.quantity) || 1,
          price: parseFloat(detail.price) || 0,
        }));
        
        //console.log('📦 Formatted items for form:', items);
        setSelectedItems(items);
      } else {
        //console.warn('⚠️ No details found in response');
        setSelectedItems([]);
      }
      
      message.success('Đã tải thông tin hóa đơn');
    } catch (error) {
      //console.error('💥 Error loading invoice detail:', error);
      
      // Enhanced error handling
      let errorMessage = 'Có lỗi xảy ra khi tải chi tiết hóa đơn';
      if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.';
        // Auto redirect to login after showing error
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (error.response?.status === 403) {
        errorMessage = 'Không có quyền truy cập hóa đơn này.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Không tìm thấy hóa đơn.';
        // Navigate back to list
        setTimeout(() => {
          navigate('/invoices/import');
        }, 2000);
      } else if (error.response?.status >= 500) {
        errorMessage = 'Lỗi server. Vui lòng thử lại sau.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const newTotal = selectedItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    setTotal(newTotal);
  };

  const handleAddItem = (product) => {
    // Debug product structure
    //console.log('🎯 Adding product:', product);
    
    // Use the most reliable ID field
    const productId = product.id || product.variant_id || product.product_id;
    //console.log('🆔 Using ID:', productId);
    
    const existingItemIndex = selectedItems.findIndex(item => item.variant_id === productId);
    
    if (existingItemIndex >= 0) {
      const newItems = [...selectedItems];
      newItems[existingItemIndex].quantity += 1;
      setSelectedItems(newItems);
      message.success(`Đã tăng số lượng ${product.product_name || product.name} ${product.variant_name || ''}`);
    } else {
      const newItem = {
        id: Date.now() + Math.random(), // Unique ID for table row
        variant_id: productId,
        product_name: product.product_name || product.name,
        variant_name: product.variant_name || product.sku,
        quantity: 1,
        price: product.import_price || product.price * 0.7, // Giá nhập ước tính 70% giá bán
      };
      
      //console.log('➕ Adding new item:', newItem);
      setSelectedItems([...selectedItems, newItem]);
      message.success(`Đã thêm ${newItem.product_name} ${newItem.variant_name || ''} vào hóa đơn`);
    }
    
    // Không đóng modal để có thể thêm nhiều sản phẩm liên tiếp
    // setProductModalVisible(false);
  };

  const handleSelectProduct = (product) => {
    console.log('=== SELECTING PRODUCT ===');
    console.log('Product to select:', { 
      id: product.id, 
      variant_id: product.variant_id,
      name: product.name,
      product_name: product.product_name,
      variant_name: product.variant_name
    });
    
    setSelectedItems(currentItems => {
      console.log('Current selected items before:', currentItems.map(item => ({ 
        id: item.id, 
        variant_id: item.variant_id,
        name: item.name 
      })));
      
      // Use the most appropriate ID - prefer variant_id if available, fallback to id
      const productId = product.variant_id || product.id;
      const isAlreadySelected = currentItems.some(item => 
        String(item.id) === String(productId) || 
        String(item.variant_id) === String(productId)
      );
      
      console.log('Product ID to use:', productId);
      console.log('Is already selected:', isAlreadySelected);
      
      if (isAlreadySelected) {
        message.info('Sản phẩm này đã được chọn');
        return currentItems; // Không thay đổi state
      }

      // Thêm sản phẩm vào danh sách đã chọn với đầy đủ thông tin
      const newItem = {
        id: productId, // Use consistent ID
        variant_id: productId, // Ensure variant_id is set for API call
        name: product.product_name || product.name || 'Unknown Product',
        variant_name: product.variant_name || product.sku || '',
        sku: product.sku || '',
        price: 0, // Giá nhập mặc định - user sẽ nhập
        quantity: 1, // Số lượng mặc định
      };

      console.log('New item to add:', newItem);

      const newSelectedItems = [...currentItems, newItem];
      console.log('New selected items after:', newSelectedItems.map(item => ({ 
        id: item.id, 
        variant_id: item.variant_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      })));
      
      message.success(`Đã thêm "${newItem.name}" vào danh sách`);
      return newSelectedItems;
    });
  };

  const handleUpdateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }
    
    const newItems = selectedItems.map(item =>
      (item.id === itemId || item.variant_id === itemId) ? { ...item, quantity } : item
    );
    setSelectedItems(newItems);
  };

  const handleUpdatePrice = (itemId, price) => {
    const newItems = selectedItems.map(item =>
      (item.id === itemId || item.variant_id === itemId) ? { ...item, price } : item
    );
    setSelectedItems(newItems);
  };

  const handleRemoveItem = (itemId) => {
    const newItems = selectedItems.filter(item => 
      item.id !== itemId && item.variant_id !== itemId
    );
    setSelectedItems(newItems);
  };

  const handleSubmit = async (values) => {
    if (selectedItems.length === 0) {
      message.error('Vui lòng thêm ít nhất một sản phẩm');
      return;
    }

    // Validate all items have required fields
    const invalidItems = selectedItems.filter(item => 
      !item.id || !item.quantity || item.quantity <= 0 || !item.price || item.price <= 0
    );
    
    if (invalidItems.length > 0) {
      message.error(`Có ${invalidItems.length} sản phẩm thiếu thông tin (variant_id, số lượng hoặc giá). Vui lòng kiểm tra lại.`);
      //console.log('❌ Invalid items:', invalidItems);
      return;
    }

    setLoading(true);
    try {
      const invoiceData = {
        supplierName: values.supplierName,
        note: values.note,
        status: 'Chưa giải quyết', // Default status
        details: selectedItems.map(item => {
          // Ensure we have proper variant_id mapping
          const variant_id = item.variant_id || item.id;
          const quantity = parseInt(item.quantity) || 0;
          const price = parseFloat(item.price) || 0;
          
          //console.log('📋 Mapping item:', { 
          //   original: item, 
          //   mapped: { variant_id, quantity, price }
          // });
          
          return {
            variant_id,
            quantity,
            price,
          };
        }),
      };

      //console.log('📋 Final invoice data:', invoiceData);

      // Validate mapped data one more time
      const invalidMappedItems = invoiceData.details.filter(detail => 
        !detail.variant_id || !detail.quantity || detail.quantity <= 0 || 
        !detail.price || detail.price <= 0
      );
      
      if (invalidMappedItems.length > 0) {
        //console.error('❌ Invalid mapped items found:', invalidMappedItems);
        message.error('Dữ liệu sản phẩm không hợp lệ sau khi mapping. Vui lòng thử lại.');
        return;
      }

      // Retry logic for API calls
      const maxRetries = 2;
      let response;
      
      const trySubmit = async (attempt = 1) => {
        try {
          if (isEditing) {
            // NEW: Use update-with-details API (replaces all details atomically)
            const updatePayload = {
              supplierName: values.supplierName,
              note: values.note,
              status: 'Chưa giải quyết',
              details: selectedItems.map(item => ({
                variant_id: item.variant_id,
                quantity: item.quantity,
                price: item.price,
              })),
            };
            //console.log(`🛠 Updating invoice with details (attempt ${attempt}):`, updatePayload);
            return await invoiceService.import.updateWithDetails(id, updatePayload);
          } else {
            // Use new API for creating
            //console.log(`📝 Creating invoice (attempt ${attempt}):`, invoiceData);
            return await invoiceService.import.createWithDetails(invoiceData);
          }
        } catch (error) {
          //console.log(`⚠️ Submit attempt ${attempt} failed:`, error.message);
          
          // Nếu là lỗi token và còn lần retry, đợi cho refresh token xử lý
          if ((error.response?.status === 401 || 
               error.response?.data?.message?.includes('Token không hợp lệ') ||
               error.response?.data?.message?.includes('đã hết hạn')) && 
              attempt <= maxRetries) {
            //console.log(`⏰ Token error detected, waiting for refresh token process...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            if (attempt < maxRetries) {
              //console.log(`🔄 Retrying submit (attempt ${attempt + 1})...`);
              return await trySubmit(attempt + 1);
            }
          }
          
          throw error;
        }
      };
      
      response = await trySubmit();

      if (response.success) {
        const successMessage = isEditing ? 'Cập nhật hóa đơn thành công' : 'Tạo hóa đơn thành công';
        
        // Enhanced success message with invoice details
        if (response.data && response.data.invoice && response.data.invoice[0]) {
          const invoice = response.data.invoice[0];
          const summary = response.data.summary;
          message.success(
            `${successMessage}! Mã hóa đơn: ${invoice.code}. ` +
            `Tổng ${summary.totalItems} sản phẩm, ${formatCurrency(summary.totalAmount)}`
          );
        } else {
          message.success(successMessage);
        }
        
        navigate('/invoices/import');
      } else {
        // Surface specific conflict message from API (e.g., only pending can edit)
        const errMsg = response.message || (isEditing ? 'Cập nhật hóa đơn thất bại' : 'Tạo hóa đơn thất bại');
        message.error(errMsg);
      }
    } catch (error) {
      //console.error('💥 Error creating/updating invoice:', error);
      
      // Enhanced error handling
      let errorMessage = isEditing ? 'Không thể cập nhật hóa đơn' : 'Không thể tạo hóa đơn';
      if (error.response?.status === 401) {
        errorMessage = 'Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.';
        // Auto redirect to login after showing error
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (error.response?.status === 403) {
        errorMessage = 'Không có quyền thực hiện thao tác này.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Lỗi server. Vui lòng thử lại sau.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Có lỗi xảy ra khi lưu hóa đơn';
      }
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.product_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    product.variant_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    product.name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    product.sku?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  //console.log('🔍 Products filtering:', {
  //   totalProducts: products.length,
  //   searchKeyword,
  //   filteredCount: filteredProducts.length
  // });

  const itemColumns = [
    {
      title: 'Sản phẩm',
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.product_name}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.variant_name}</div>
        </div>
      ),
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (quantity, record) => (
        <InputNumber
          min={1}
          value={quantity}
          onChange={(value) => handleUpdateQuantity(record.id, value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Giá nhập',
      dataIndex: 'price',
      key: 'price',
      width: 150,
      render: (price, record) => (
        <InputNumber
          min={0}
          value={price}
          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value.replace(/\$\s?|(,*)/g, '')}
          onChange={(value) => handleUpdatePrice(record.id, value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Thành tiền',
      key: 'total',
      width: 150,
      align: 'right',
      render: (_, record) => (
        <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
          {formatCurrency(record.quantity * record.price)}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(record.id)}
        />
      ),
    },
  ];

  const productColumns = [
    {
      title: 'Sản phẩm',
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.product_name || record.name}</div>
          <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>{record.variant_name}</div>
          <div style={{ color: '#999', fontSize: '11px' }}>
            <strong>SKU:</strong> {record.sku || 'Không có'}
          </div>
        </div>
      ),
    },
    {
      title: 'Giá bán hiện tại',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      align: 'right',
      render: (price) => formatCurrency(price),
    },
    {
      title: 'Tồn kho',
      dataIndex: 'stock',
      key: 'stock',
      align: 'center',
      render: (stock) => (
        <Tag color={stock > 10 ? 'green' : stock > 0 ? 'orange' : 'red'}>
          {stock}
        </Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      render: (_, record) => {
        // Use same ID logic as handleAddItem
        const productId = record.id || record.variant_id || record.product_id;
        const existingItem = selectedItems.find(item => item.variant_id === productId);
        const isSelected = !!existingItem;
        
        // Debug log - remove in production
        if (selectedItems.length > 0) {
          //console.log('🔍 Button render check:', {
          //   recordId: productId,
          //   recordName: record.product_name || record.name,
          //   selectedItemIds: selectedItems.map(item => item.variant_id),
          //   isSelected,
          //   existingItemQty: existingItem?.quantity
          // });
        }
        
        return (
          <Space>
            <Button
              type={isSelected ? "default" : "primary"}
              size="small"
              onClick={() => handleAddItem(record)}
              disabled={false}
            >
              {isSelected ? `Đã chọn (${existingItem.quantity})` : 'Chọn'}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: isMobile ? 8 : 24 }}>
      <Card>
        <div style={{ 
          marginBottom: 16, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 0
        }}>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            {isEditing ? `✏️ Chỉnh sửa hóa đơn nhập` : '📝 Tạo hóa đơn nhập'}
            {isEditing && loading && <Spin style={{ marginLeft: 12 }} size="small" />}
          </Title>
          <Button 
            onClick={() => navigate('/invoices/import')}
            size={isMobile ? 'middle' : 'default'}
            block={isMobile}
          >
            Quay lại
          </Button>
        </div>

        {/* Debug info */}
        {isEditing && (
          <div style={{ background: '#f5f5f5', padding: '8px', marginBottom: '16px', borderRadius: '4px', fontSize: '12px' }}>
            <div>Debug: ID={id}, Loading={loading.toString()}, SelectedItems={selectedItems.length}</div>
            {selectedItems.length > 0 && (
              <div style={{ marginTop: '4px' }}>
                <strong>Form Debug:</strong>
                <div>Supplier Name: {form.getFieldValue('supplierName') || 'EMPTY'}</div>
                <div>Note: {form.getFieldValue('note') || 'EMPTY'}</div>
              </div>
            )}
          </div>
        )}

        {/* Show loading spinner when loading invoice details in edit mode */}
        {isEditing && loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: '#666' }}>
              Đang tải thông tin hóa đơn với ID: {id}
            </div>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
              Nếu loading quá lâu, vui lòng kiểm tra //console để debug
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
          <Row gutter={isMobile ? [0, 16] : 16}>
            <Col xs={24} md={12}>
              <Card 
                title={<><UserOutlined /> Thông tin nhà cung cấp</>} 
                size="small"
                style={{ marginBottom: isMobile ? 16 : 0 }}
              >
                <Form.Item
                  name="supplierName"
                  label="Tên nhà cung cấp"
                  rules={[{ required: true, message: 'Vui lòng nhập tên nhà cung cấp' }]}
                >
                  <Input placeholder="Nhập tên nhà cung cấp" />
                </Form.Item>

                {/* Removed supplierPhone and supplierAddress per DB schema */}

                <Form.Item name="note" label="Ghi chú">
                  <TextArea rows={3} placeholder="Ghi chú cho hóa đơn nhập" />
                </Form.Item>
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card 
                title="💰 Thông tin thanh toán" 
                size="small"
                style={{ marginTop: isMobile ? 16 : 0 }}
              >
                <div style={{ textAlign: 'center', padding: isMobile ? '20px 0' : '40px 0' }}>
                  <div style={{ fontSize: isMobile ? '14px' : '18px', color: '#666', marginBottom: 8 }}>
                    Tổng tiền hóa đơn
                  </div>
                  <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: 'bold', color: '#52c41a' }}>
                    {formatCurrency(total)}
                  </div>
                  <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#999', marginTop: 8 }}>
                    📦 {selectedItems.length} sản phẩm được chọn
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Card 
            title={
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span><ShoppingCartOutlined /> Danh sách sản phẩm</span>
                {isMobile && (
                  <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                    📱 Nhấn "Thêm sản phẩm" để chọn
                  </span>
                )}
              </div>
            }
            style={{ marginTop: 16 }}
            extra={
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setProductModalVisible(true)}
                size={isMobile ? 'small' : 'default'}
              >
                {isMobile ? 'Thêm SP' : 'Thêm sản phẩm'}
              </Button>
            }
          >
            {isMobile && selectedItems.length > 0 ? (
              <div>
                {selectedItems.map(item => (
                  <Card
                    key={item.id}
                    size="small"
                    style={{
                      marginBottom: 12,
                      border: '1px solid #f0f0f0',
                      borderRadius: 8
                    }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: 4 }}>
                          {item.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                          SKU: {item.sku}
                        </div>
                      </div>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveItem(item.id)}
                      />
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>Số lượng</div>
                        <InputNumber
                          min={1}
                          value={item.quantity}
                          onChange={(value) => handleUpdateQuantity(item.id, value)}
                          style={{ width: '100%' }}
                          size="small"
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>Giá nhập</div>
                        <InputNumber
                          min={0}
                          value={item.price}
                          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={value => value.replace(/\$\s?|(,*)/g, '')}
                          onChange={(value) => handleUpdatePrice(item.id, value)}
                          style={{ width: '100%' }}
                          size="small"
                        />
                      </div>
                    </div>
                    
                    <div style={{ textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                      <div style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '14px' }}>
                        Thành tiền: {formatCurrency(item.quantity * item.price)}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : !isMobile ? (
              <Table
                columns={itemColumns}
                dataSource={selectedItems}
                rowKey="id"
                pagination={false}
                locale={{ emptyText: 'Chưa có sản phẩm nào được chọn' }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                <div>Chưa có sản phẩm nào được chọn</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>
                  Nhấn nút "Thêm SP" để chọn sản phẩm
                </div>
              </div>
            )}
          </Card>

          <div style={{ 
            textAlign: isMobile ? 'center' : 'right', 
            marginTop: 24,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'center' : 'flex-end',
            gap: isMobile ? 12 : 0
          }}>
            <Space direction={isMobile ? 'vertical' : 'horizontal'} size={isMobile ? 0 : 'middle'}>
              <Button 
                onClick={() => navigate('/invoices/import')}
                size={isMobile ? 'large' : 'default'}
                block={isMobile}
              >
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                disabled={selectedItems.length === 0}
                size={isMobile ? 'large' : 'default'}
                block={isMobile}
              >
                {isEditing ? 'Cập nhật hóa đơn' : 'Tạo hóa đơn'}
              </Button>
            </Space>
          </div>
        </Form>
        )}
      </Card>

      {/* Modal chọn sản phẩm */}
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 8 : 0
          }}>
            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>🛍️ Chọn sản phẩm</span>
            <Tag color="blue" style={{ fontSize: isMobile ? '12px' : '14px' }}>
              Đã chọn: {selectedItems.length} sản phẩm
            </Tag>
          </div>
        }
        open={productModalVisible}
        onCancel={() => setProductModalVisible(false)}
        footer={
          <div style={{ textAlign: 'center' }}>
            <Button 
              type="primary" 
              onClick={() => setProductModalVisible(false)}
              size={isMobile ? 'large' : 'default'}
              block={isMobile}
            >
              ✅ Hoàn tất chọn sản phẩm ({selectedItems.length})
            </Button>
          </div>
        }
        width={isMobile ? '95%' : 800}
        style={isMobile ? { top: 20 } : {}}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 8,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 8 : 0
          }}>
            <Input
              placeholder="Tìm kiếm sản phẩm..."
              prefix={<SearchOutlined />}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              allowClear
              style={{ flex: 1, marginRight: isMobile ? 0 : 16 }}
              size={isMobile ? 'middle' : 'default'}
            />
            <Tag color="green" style={{ fontSize: isMobile ? '12px' : '14px' }}>
              {filteredProducts.length} / {products.length} sản phẩm
            </Tag>
          </div>
        </div>
        
        {/* Mobile Product Grid or Desktop Table */}
        {isMobile ? (
          <div style={{ 
            maxHeight: '500px', 
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '12px',
              padding: '8px'
            }}>
              {filteredProducts.map((product, index) => {
                // Debug product structure
                console.log(`Product ${index} structure:`, product);
                
                // Handle different possible product structures
                const productId = product.id || product.product_id || product.variant_id;
                const productName = product.name || product.product_name || product.variant_name;
                const productSku = product.sku;
                
                console.log(`Product ${index} parsed:`, { id: productId, name: productName, sku: productSku });
                
                // Use a more explicit check with current selectedItems
                const isSelected = selectedItems.some(item => {
                  const itemId = String(item.id);
                  const currentProductId = String(productId);
                  return itemId === currentProductId;
                });
                
                console.log(`Product ${index} (${productId}): ${productName} - Selected: ${isSelected}`);
                
                return (
                  <div
                    key={`product-${productId}-${index}`}
                    onClick={() => {
                      console.log(`=== CLICKED PRODUCT ${index} ===`);
                      console.log('Product ID:', productId, 'Name:', productName);
                      console.log('Is Selected:', isSelected);
                      console.log('Raw product object:', product);
                      
                      if (!isSelected && productId && productName) {
                        const productToSelect = {
                          id: productId,
                          name: productName,
                          sku: productSku,
                          // Include other fields that might be needed
                          ...product
                        };
                        handleSelectProduct(productToSelect);
                      } else if (isSelected) {
                        console.log('Product already selected, ignoring click');
                      } else {
                        console.log('Product missing ID or name, cannot select');
                      }
                    }}
                    style={{
                      border: `2px solid ${isSelected ? '#52c41a' : '#d9d9d9'}`,
                      borderRadius: '12px',
                      padding: '12px',
                      backgroundColor: isSelected ? '#f6ffed' : '#fff',
                      cursor: isSelected ? 'default' : 'pointer',
                      transition: 'all 0.3s',
                      opacity: isSelected ? 0.8 : 1,
                      minHeight: '140px',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: isSelected ? '0 4px 12px rgba(82, 196, 26, 0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
                      transform: isSelected ? 'scale(0.98)' : 'scale(1)'
                    }}
                  >
                    {/* Product Info */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        {/* Product Name - Prominent at top */}
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: 'bold', 
                          color: isSelected ? '#52c41a' : '#333',
                          marginBottom: '8px',
                          lineHeight: '1.3',
                          height: 'auto',
                          minHeight: '36px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {productName || 'Tên sản phẩm không có'}
                        </div>
                        
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#666',
                          marginBottom: '6px',
                          backgroundColor: '#f0f0f0',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}>
                          <strong>SKU:</strong> {productSku || 'Không có'}
                        </div>

                        {/* Attributes Display */}
                        {(() => {
                          const attributes = product.attributes || product.variant_attributes || [];
                          return Array.isArray(attributes) && attributes.length > 0 ? (
                            <div style={{ marginBottom: '6px' }}>
                              {attributes.slice(0, 2).map((attr, attrIndex) => (
                                <div
                                  key={attrIndex}
                                  style={{
                                    fontSize: '9px',
                                    color: '#1890ff',
                                    backgroundColor: '#e6f7ff',
                                    padding: '2px 4px',
                                    borderRadius: '3px',
                                    display: 'inline-block',
                                    marginRight: '4px',
                                    marginBottom: '2px',
                                    border: '1px solid #91d5ff'
                                  }}
                                >
                                  {attr.name || attr.attribute_name}: {attr.value || attr.attribute_value}
                                </div>
                              ))}
                              {attributes.length > 2 && (
                                <div style={{
                                  fontSize: '9px',
                                  color: '#999',
                                  display: 'inline-block'
                                }}>
                                  +{attributes.length - 2} khác
                                </div>
                              )}
                            </div>
                          ) : null;
                        })()}

                        {product.category_name && (
                          <div style={{ 
                            fontSize: '10px', 
                            color: '#999',
                            marginBottom: '4px'
                          }}>
                            📂 {product.category_name}
                          </div>
                        )}

                        {product.variants && product.variants.length > 0 && (
                          <div style={{ 
                            fontSize: '10px', 
                            color: '#666',
                            marginBottom: '4px'
                          }}>
                            🏷️ {product.variants.length} biến thể
                          </div>
                        )}
                      </div>

                      <div style={{ 
                        fontSize: '11px', 
                        color: isSelected ? '#52c41a' : '#1890ff',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        padding: '8px',
                        backgroundColor: isSelected ? 'rgba(82, 196, 26, 0.1)' : 'rgba(24, 144, 255, 0.1)',
                        borderRadius: '6px',
                        marginTop: '8px'
                      }}>
                        {isSelected ? '✓ ĐÃ CHỌN' : '👆 NHẤN ĐỂ CHỌN'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {filteredProducts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                <div>Không tìm thấy sản phẩm phù hợp</div>
                <div style={{ fontSize: '12px', marginTop: '8px', color: '#666' }}>
                  Thử từ khóa khác hoặc kiểm tra chính tả
                </div>
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={productColumns}
            dataSource={filteredProducts}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="small"
            locale={{ 
              emptyText: products.length === 0 ? 'Không có sản phẩm nào. Kiểm tra API hoặc token.' : 'Không tìm thấy sản phẩm phù hợp.' 
            }}
          />
        )}
        
       
       
      </Modal>
    </div>
  );
};

export default InvoiceImportForm;
